import React from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { submit, submitPut } from "../../../../../../enums/queryFunction/Submit";
import { deleteCore } from "../../../../../../enums/queryFunction/Delete";
import { SnackbarContext } from "../../../../../../contexts/Snackbar";
import { snackbarSeverity } from "../../../../../../hooks/useSnackbar";

export default function useWarehouseNamedEntityMutations({ createUrl, entityBaseUrl, queryKey, invalidateOnDelete = [] }) {
  const { handleSnackbar } = React.useContext(SnackbarContext);
  const queryClient = useQueryClient();

  function addToCache(entity) {
    queryClient.setQueryData(queryKey, (old) => (Array.isArray(old) ? [...old, entity] : [entity]));
  }

  function replaceInCache(entity) {
    queryClient.setQueryData(queryKey, (old) =>
      Array.isArray(old) ? old.map((e) => (e.id === entity.id ? entity : e)) : old,
    );
  }

  function removeFromCache(id) {
    queryClient.setQueryData(queryKey, (old) => (Array.isArray(old) ? old.filter((e) => e.id !== id) : old));
  }

  const createMutation = useMutation({ mutationFn: (data) => submit(createUrl, data) });
  const updateMutation = useMutation({ mutationFn: ({ id, data }) => submitPut(`${entityBaseUrl}${id}`, data) });
  const deleteMutation = useMutation({ mutationFn: (id) => deleteCore(entityBaseUrl, id) });

  async function create(data) {
    try {
      const response = await createMutation.mutateAsync(data);
      if (response?.data) {
        addToCache(response.data);
      }
      handleSnackbar("success", snackbarSeverity.SUCCESS);
      return true;
    } catch (error) {
      handleSnackbar(error, "error");
      return false;
    }
  }

  async function update(id, data) {
    try {
      const response = await updateMutation.mutateAsync({ id, data });
      if (response?.data) {
        replaceInCache(response.data);
      }
      handleSnackbar("success", snackbarSeverity.SUCCESS);
      return true;
    } catch (error) {
      handleSnackbar(error, "error");
      return false;
    }
  }

  async function remove(id) {
    try {
      await deleteMutation.mutateAsync(id);
      removeFromCache(id);
      // Deleting an in-use category/supplier clears that link on the
      // affected warehouse items server-side - invalidate so their cached
      // category/supplier chips don't keep showing the deleted one.
      invalidateOnDelete.forEach((key) =>
        queryClient.invalidateQueries({ queryKey: Array.isArray(key) ? key : [key] }),
      );
      handleSnackbar("success", snackbarSeverity.SUCCESS);
      return true;
    } catch (error) {
      handleSnackbar(error, "error");
      return false;
    }
  }

  return {
    create,
    isCreating: createMutation.isPending,
    update,
    isUpdating: updateMutation.isPending,
    remove,
    isDeleting: deleteMutation.isPending,
  };
}
